import React, { useEffect, useRef, useState } from "react";
import { io, Socket as SocketType } from "socket.io-client";
import { useAppDispatch, useAppSelector } from "App/store/hooks";
import { updateFleetPosition } from "App/store/slices/fleet.slice";
import { updateBookingStatus, updateTrackingPosition } from "App/store/slices/bookings.slice";
import { showToast } from "App/store/slices/toast.slice";

/**
 * The app's single Socket.IO connection to the `soc` service. Mounted once at
 * the root; components read the resulting state out of the store rather than
 * opening sockets of their own.
 */
const SocketComponent: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const zone = useAppSelector((state) => state.user.zone);
  const [isConnected, setIsConnected] = useState(false);

  const socketRef = useRef<SocketType | null>(null);
  // The connect effect keys only on user id/type, so it cannot read `zone`
  // directly without capturing a stale value. Mirror it into a ref that stays
  // current and re-register when the zone first resolves or later changes.
  const zoneIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!user?.id) return;
    if (socketRef.current?.connected) return;

    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socketUrl = process.env.REACT_APP_SOC_API_URL;
    if (!socketUrl) {
      console.error("Socket URL is not defined in environment variables");
      return;
    }

    const socketInstance = io(socketUrl, {
      transports: ["websocket", "polling"],
      forceNew: false,
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
    });

    const registerUser = () => {
      // zoneId must be sent. Omitting it makes the server evaluate
      // `undefined !== ''` as true, so the client joins the literal room
      // "zone_undefined": it receives none of the real zone_<n> traffic and
      // shares one cross-tenant room with every other zone.
      const zoneId = zoneIdRef.current;
      socketInstance.emit("registerCustomer", {
        id: user.id,
        type: user.type,
        ...(zoneId ? { zoneId: String(zoneId) } : {}),
      });
    };

    socketInstance.on("connect", () => {
      setIsConnected(true);
      registerUser();
    });

    socketInstance.on("reconnect", () => {
      registerUser();
    });

    socketInstance.on("disconnect", () => {
      setIsConnected(false);
    });

    socketInstance.on("connect_error", (error: any) => {
      console.error("Socket connection error:", error);
      setIsConnected(false);
    });

    // Server-initiated sign-out for this session id.
    socketInstance.on("forceLogout", (data: { hid: number }) => {
      try {
        const token = localStorage.getItem("userToken");
        if (!token) return;
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload.hid === data.hid) {
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          localStorage.removeItem("userToken");
          window.location.reload();
        }
      } catch (e) {
        console.error("forceLogout handler error:", e);
      }
    });

    // --- Fleet: live position and availability for the agency's cars ---
    socketInstance.on("driverLocation", (data: any) => {
      dispatch(updateFleetPosition(data));
    });

    socketInstance.on("driverStatusChange", (data: any) => {
      dispatch(updateFleetPosition(data));
    });

    // --- Rides: status transitions and the trail of a ride in progress ---
    socketInstance.on("bookingStatusChange", (data: any) => {
      dispatch(updateBookingStatus(data));
    });

    socketInstance.on("bookingAccepted", (data: any) => {
      dispatch(updateBookingStatus({ ...data, status: "active" }));
      dispatch(
        showToast({ type: "success", head: "Ride accepted", text: "A driver accepted the ride." })
      );
    });

    socketInstance.on("bookingCancelled", (data: any) => {
      dispatch(updateBookingStatus({ ...data, status: "cancelled" }));
    });

    socketInstance.on("tripLocation", (data: any) => {
      dispatch(updateTrackingPosition(data));
    });

    socketRef.current = socketInstance;

    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
    };
  }, [user?.id, user?.type, dispatch]);

  // Keep zoneIdRef fresh and re-register when the zone becomes known or
  // changes, so the socket joins the correct zone_<n> room rather than staying
  // in whatever room it joined at connect time.
  useEffect(() => {
    const socket = socketRef.current;
    const nextZoneId = zone?.zone_id ?? user?.details?.zone_id;
    if (zoneIdRef.current === nextZoneId) return;
    zoneIdRef.current = nextZoneId;
    if (socket && isConnected && user?.id && nextZoneId) {
      socket.emit("registerCustomer", {
        id: user.id,
        type: user.type,
        zoneId: String(nextZoneId),
      });
    }
  }, [zone?.zone_id, user?.details?.zone_id, isConnected, user?.id, user?.type]);

  return null;
};

export default SocketComponent;
